import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  MessageSquare,
  Users,
  Building,
  TrendingUp,
  Calendar,
  Loader2,
  AlertCircle,
  CalendarDays,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useDashboard } from "@/hooks/useDashboard";
import { DashboardMetricsRequest } from "@/types/api";
import { cn } from "@/lib/utils";

const Dashboard = () => {
  const [selectedPeriod, setSelectedPeriod] =
    useState<DashboardMetricsRequest["period"]>("last30days");
  const [showComparison, setShowComparison] = useState(true);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);

  // Integração com o backend
  const { metrics, isLoading, error } = useDashboard({
    period: selectedPeriod,
    compareWithPrevious: showComparison,
    startDate: selectedPeriod === "custom" ? customStartDate : undefined,
    endDate: selectedPeriod === "custom" ? customEndDate : undefined,
  });

  // Função para calcular mudança percentual
  const calculatePercentageChange = (current: number, previous?: number) => {
    if (!previous || previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const formatPercentage = (percentage: number) => {
    const sign = percentage >= 0 ? "+" : "";
    return `${sign}${percentage.toFixed(1)}%`;
  };

  // Funções para lidar com datas personalizadas
  const handlePeriodChange = (value: DashboardMetricsRequest["period"]) => {
    setSelectedPeriod(value);
    if (value !== "custom") {
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setSelectedPeriod("custom");
      setIsCustomDateOpen(false);
    }
  };

  const formatCustomDateRange = () => {
    if (selectedPeriod === "custom" && customStartDate && customEndDate) {
      const start = new Date(customStartDate).toLocaleDateString("pt-BR");
      const end = new Date(customEndDate).toLocaleDateString("pt-BR");
      return `${start} - ${end}`;
    }
    return null;
  };

  const isCustomDateValid = () => {
    if (!customStartDate || !customEndDate) return false;
    return new Date(customStartDate) <= new Date(customEndDate);
  };

  // Transformar dados do backend para o formato dos gráficos
  const getChartData = () => {
    if (!metrics) return { messageStats: [], dailyMessages: [] };

    // Dados para gráfico de barras (mensagens por dia)
    const messageStats =
      metrics.mensagensPorDia?.map((item) => ({
        month: item.dia,
        messages: item.mensagens,
        previous: item.anterior || 0,
      })) || [];

    // Dados para gráfico de linha (atividade por hora)
    const dailyMessages =
      metrics.atividadePorHora?.map((item) => ({
        day: item.hora,
        messages: item.mensagens,
      })) || [];

    return { messageStats, dailyMessages };
  };

  const currentData = getChartData();

  // Dados simulados para tipos de conversa (não disponível no backend atual)
  const conversationTypes = [
    { name: "Suporte", value: 45, color: "#3B82F6" },
    { name: "Vendas", value: 30, color: "#10B981" },
    { name: "Consultas", value: 25, color: "#F59E0B" },
  ];

  // KPIs baseados nos dados do backend
  const getKpiCards = () => {
    if (!metrics) return [];

    const userChange = calculatePercentageChange(
      metrics.userCount,
      metrics.previousUserCount
    );
    const companyChange = calculatePercentageChange(
      metrics.companyCount,
      metrics.previousCompanyCount
    );

    // Calcular total de mensagens do período
    const totalMessages =
      metrics.mensagensPorDia?.reduce((sum, item) => sum + item.mensagens, 0) ||
      0;
    const previousMessages =
      metrics.mensagensPorDia?.reduce(
        (sum, item) => sum + (item.anterior || 0),
        0
      ) || 0;
    const messageChange = calculatePercentageChange(
      totalMessages,
      previousMessages
    );

    return [
      {
        title: "Total de Mensagens",
        value: totalMessages.toLocaleString(),
        icon: MessageSquare,
        change: formatPercentage(messageChange),
        color: "text-blue-600",
      },
      {
        title: "Usuários Ativos",
        value: metrics.userCount.toLocaleString(),
        icon: Users,
        change: formatPercentage(userChange),
        color: "text-green-600",
      },
      {
        title: "Empresas",
        value: metrics.companyCount.toLocaleString(),
        icon: Building,
        change: formatPercentage(companyChange),
        color: "text-purple-600",
      },
      {
        title: "Taxa de Resposta",
        value: "94.2%", // Valor simulado - não disponível no backend atual
        icon: TrendingUp,
        change: "+2.1%",
        color: "text-orange-600",
      },
    ];
  };

  const kpiCards = getKpiCards();

  const periodOptions = [
    { value: "today", label: "Hoje" },
    { value: "yesterday", label: "Ontem" },
    { value: "last7days", label: "Últimos 7 dias" },
    { value: "last30days", label: "Últimos 30 dias" },
    { value: "custom", label: "Período personalizado" },
  ];

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Erro ao carregar métricas:{" "}
            {(error as any)?.message || "Erro desconhecido"}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Visão geral das estatísticas de mensagens
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Filtro de Período */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Selecionar período" />
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Seletor de Data Personalizada */}
          {selectedPeriod === "custom" && (
            <Popover open={isCustomDateOpen} onOpenChange={setIsCustomDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-60 justify-start text-left font-normal",
                    !formatCustomDateRange() && "text-muted-foreground"
                  )}
                >
                  <CalendarDays className="mr-2 h-4 w-4" />
                  {formatCustomDateRange() || "Selecionar datas"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Data inicial</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      max={customEndDate || undefined}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">Data final</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      min={customStartDate || undefined}
                      max={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleCustomDateApply}
                      disabled={!isCustomDateValid()}
                      size="sm"
                      className="flex-1"
                    >
                      Aplicar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setIsCustomDateOpen(false)}
                      size="sm"
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                  </div>
                  {!isCustomDateValid() && customStartDate && customEndDate && (
                    <p className="text-sm text-destructive">
                      A data inicial deve ser anterior à data final
                    </p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Toggle de Comparação */}
          <div className="flex items-center gap-2">
            <Switch
              id="comparison-toggle"
              checked={showComparison}
              onCheckedChange={setShowComparison}
            />
            <Label htmlFor="comparison-toggle" className="text-sm font-medium">
              Comparar com período anterior
            </Label>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Carregando métricas...</span>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {kpiCards.map((kpi, index) => {
              const Icon = kpi.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">
                          {kpi.title}
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {kpi.value}
                        </p>
                        {showComparison && (
                          <p className={`text-sm ${kpi.color}`}>
                            {kpi.change} vs período anterior
                          </p>
                        )}
                      </div>
                      <Icon className={`h-12 w-12 ${kpi.color}`} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Mensagens por Período</CardTitle>
                <CardDescription>
                  Evolução das mensagens ao longo do tempo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={currentData.messageStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="messages" fill="#3B82F6" name="Atual" />
                    {showComparison && (
                      <Bar dataKey="previous" fill="#94A3B8" name="Anterior" />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tipos de Conversas</CardTitle>
                <CardDescription>Distribuição por categoria</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={conversationTypes}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}%`}
                    >
                      {conversationTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Atividade no Período</CardTitle>
              <CardDescription>
                Padrão de atividade no período selecionado
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={currentData.dailyMessages}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="messages"
                    stroke="#10B981"
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Dashboard;
